import { createRootRouteWithContext } from '@tanstack/react-router';
import { authnClient } from '@frontend/auth';
import {
  getJTI,
  getLoginData,
  getUser,
  getWeaveToken,
  isWeaveTokenBufferActive,
  type PortalUser,
} from '@frontend/auth-helpers';
import { http } from '@frontend/fetch';
import { sentry } from '@frontend/tracking';

type Context = {
  authenticated: boolean;
  user: PortalUser | undefined;
  lastLocationId: string | undefined;
};

export const Route = createRootRouteWithContext<Context>()({
  beforeLoad: async () => {
    const token = getWeaveToken();
    const jti = getJTI();
    const useJTI = await authnClient.shouldUseJTI();
    http.setUseJti(useJTI);
    http.setAuthorizationHeader(token, jti);
    const authenticated = authnClient.isUserAuthenticated() || isWeaveTokenBufferActive();
    if (!authnClient.storedUserIsLoggedInUser()) {
      console.warn('User ID mismatch between stored user and token, fetching user');
      sentry.error({
        topic: 'auth',
        team: 'users-infra',
        error: 'User ID mismatch between stored user and token',
        sampleRate: 1,
      });
      await authnClient.fetchUser();
    }
    const user: PortalUser | undefined = token ? getUser() ?? (await authnClient.fetchUser()) : undefined;
    const lastLocationId = user ? getLoginData(user.userID)?.latestLocationIds?.[0] : undefined;
    /**
     * Data returned here will be available on each route, accessible via `useMatch`
     * We could test for page access here as well (authenticated but not authorized)
     */
    return { authenticated, user, lastLocationId };
  },
});
